import { Content, Task } from '@amzn/awscat-pattern-library-service-model';

import { reorderById } from '../common/Arrays';
import { tryUpdateEpic } from './EpicOperations';

/**
 * Appends `task` to the given epic's tasks. Returns `undefined` when
 * the epic does not exist, or when a task with the same id already
 * exists inside the epic.
 */
export function tryAddTask(content: Content, epicId: string, task: Task): Content | undefined {
  let duplicate = false;

  const result = tryUpdateEpic(content, epicId, (epic) => {
    const tasks = epic.tasks ?? [];

    // Duplicate guard — may happen via a race.
    if (tasks.some((t) => t.id === task.id)) {
      duplicate = true;
      return epic;
    }

    return { ...epic, tasks: [...tasks, task] };
  });

  if (result && duplicate) return undefined;
  return result;
}

/**
 * Replaces the task identified by `taskId` inside `epicId` with
 * `setter(current)`. Returns `undefined` when the epic or the task
 * does not exist.
 */
export function tryUpdateTask(
  content: Content,
  epicId: string,
  taskId: string,
  setter: (task: Task) => Task,
): Content | undefined {
  let found = false;

  const result = tryUpdateEpic(content, epicId, (epic) => {
    const tasks = epic.tasks ?? [];
    const index = tasks.findIndex((t) => t.id === taskId);
    if (index < 0) return epic;

    found = true;
    const next = tasks.slice();
    // @ts-expect-error TS2345 - strict migration
    next[index] = setter(tasks[index]);
    return { ...epic, tasks: next };
  });

  // If the epic was found but the task wasn't, signal "not found".
  if (result && !found) return undefined;
  return result;
}

/**
 * Removes the task identified by `taskId` from `epicId`. Returns
 * `undefined` when the epic or the task does not exist.
 */
export function tryDeleteTask(
  content: Content,
  epicId: string,
  taskId: string,
): Content | undefined {
  let found = false;

  const result = tryUpdateEpic(content, epicId, (epic) => {
    const tasks = epic.tasks ?? [];
    if (!tasks.some((t) => t.id === taskId)) return epic;

    found = true;
    return { ...epic, tasks: tasks.filter((t) => t.id !== taskId) };
  });

  // If the epic was found but the task wasn't, signal "not found".
  if (result && !found) return undefined;
  return result;
}

/**
 * Reorders the tasks within `epicId` to match `newOrderIds`. Returns
 * `undefined` when the epic does not exist.
 */
export function tryReorderTasks(
  content: Content,
  epicId: string,
  newOrderIds: readonly string[],
): Content | undefined {
  return tryUpdateEpic(content, epicId, (epic) => ({
    ...epic,
    tasks: reorderById(epic.tasks ?? [], newOrderIds),
  }));
}
